import { useState, useEffect, useRef } from "react";
import { debounce } from "lodash";
import TextField from "@mui/material/TextField";
import {
    Box,
    Typography,
    Autocomplete,
    Button,
    Switch,
    CircularProgress,
} from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import HorizontalFlex from "components/lib-ui/HorizontalFlex";
import { useForm, Controller } from "react-hook-form";
import Stack from "@mui/material/Stack";
import { GlobalAppDataStorageService } from "infra/storage";
import { useSelector } from "react-redux";
import { useExperiences } from "hooks/useExperiences";
import LoadingButton from "@mui/lab/LoadingButton";
import SaveIcon from "@mui/icons-material/Save";
import useInsertCompany from "hooks/useInsertCompany";
import useFetchCompanies from "hooks/useFetchCompanies";
import MeteredButton from "components/lib-ui/MeteredButton";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import GeneralUtilityService from "services/GeneralUtilityService";
import useSearchLocations from "hooks/api/useSearchLocations";
import EditIcon from "@mui/icons-material/Edit";
export const ExperienceForm = ({
    experience,
    title,
    edit,
    setIsDialogOpen,
}) => {
    const candidateId = useSelector((state) => state?.currentCandidate?.id);
    const experienceId = experience?.id;
    const experienceEndDate = experience?.endAt;
    const locationTitle = experience?.location?.title;
    const [isLocationUpdated, setIsLocationUpdated] = useState(false);

    const startAtToMonth = GeneralUtilityService.convertDateToMonthInput(
        experience?.startAt,
    );
    const endAtToMonth = GeneralUtilityService.convertDateToMonthInput(
        experience?.endAt,
    );

    const { handleSubmit, control, register, watch, setValue, getValues } =
        useForm({
            defaultValues: {
                startAt: edit ? startAtToMonth : null,
                endAt: edit ? endAtToMonth : null,
            },
        });
    const [showExtraOptions, setShowExtraOptions] = useState(false);
    const currentlyWorkingStatus = watch("currentlyWorking");
    const [isCurrentlyWorking, setIsCurrentlyWorking] = useState(
        experienceEndDate === null,
    );

    useEffect(() => {
        if (currentlyWorkingStatus) {
            setValue("endAt", null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentlyWorkingStatus]);

    const { insertCompany } = useInsertCompany();
    const { fetching, fetchCompaniesByName, companiesOptions } =
        useFetchCompanies();
    const {
        addNewExperience,
        editExperience,
        deleteExperience,
        isLoading,
        isDeleteLoading,
        addRequestSuccessful,
        editRequestSuccessful,
        deleteRequestSuccessful,
    } = useExperiences();

    const {
        isSearchLocationsLoading,
        locations,
        fetchLocations,
        setLocations,
    } = useSearchLocations();

    const jobTypeOptions = GlobalAppDataStorageService.getJobTypes();
    const hardSkillsOptions = GlobalAppDataStorageService.getHardSkills();
    const companySizeOptions = GlobalAppDataStorageService.getCompanySizes();
    const companyTypesOptions = GlobalAppDataStorageService.getCompanyTypes();
    const industriesOptions = GlobalAppDataStorageService.getIndustries();
    const jobChangeReasonsOptions =
        GlobalAppDataStorageService.getJobChangeReasons();

    const occupationsOptions = GlobalAppDataStorageService.getOccupations();
    const jobFunctionsOptions = GlobalAppDataStorageService.getJobFunctions();
    const handleClick = (event) => {
        setShowExtraOptions(!showExtraOptions);
        // Focusing on the button causes the search field to blur
        event.target.focus();
    };

    const shouldFetchCompanies = (input) => {
        return Boolean(input?.trim()?.length >= 2);
    };

    const handleLocationInputChange = (event, value, reason) => {
        if (reason === "clear") {
            setLocations([]);
        }

        if (reason === "input") {
            fetchLocations(value);
        }
    };

    const submitForm = async (data) => {
        const endAtToMonth =
            GeneralUtilityService.convertMonthInputToDate(data.endAt) || null;
        const startAtToMonth =
            GeneralUtilityService.convertMonthInputToDate(data.startAt) || null;
        data.endAt === true
            ? setIsCurrentlyWorking(true)
            : setIsCurrentlyWorking(false);
        const newCompanyId = showExtraOptions
            ? await insertCompany(data)
            : null;
        const dataIds = {
            companyId: newCompanyId ? newCompanyId : data?.companyId?.id,
            jobTitle: data?.jobTitle,
            jobDescription: data?.jobDescription,
            startAt: startAtToMonth,
            endAt: endAtToMonth,
            location: {
                coordinates: { ...data?.location?.coordinates },
                locationInfo: { ...data?.location?.locationInfo },
            },
            skills: data?.skills?.map((item) => item.id),
            industries: data?.industries?.map((item) => item.id),
            occupations: data?.occupations.map((item) => item.id),
            jobFunctions: data?.jobFunctions.map((item) => item.id),
            jobTypeId: data?.jobType?.id,
            workingModelId: data?.workingModelId?.id,
            jobChangeReasonId: data?.jobChangeReason?.id,
            teamSize: data?.teamSize,
        };

        if(data?.location === undefined ) {
            delete dataIds.location;
        }

        if (!edit) {
            await addNewExperience(candidateId, dataIds);
        }
        if (edit) {
            const endpointParams = {
                candidateId: candidateId,
                experienceId: experienceId,
            };

            await editExperience(endpointParams, dataIds);
        }
    };

    const debouncedFetchCompanyByName = useRef(
        debounce((value) => {
            fetchCompaniesByName(value);
        }, 500),
    ).current;

    const handleCompanyFetch = (event) => {
        if (shouldFetchCompanies(event.target.value))
            debouncedFetchCompanyByName(event.target.value);
    };

    const [renderNoOptionsComponent, setRenderNoOptionsComponent] =
        useState("Start typing");

    useEffect(() => {
        const companyNameValue = getValues("companyId")?.name;
        if (shouldFetchCompanies(companyNameValue))
            fetchCompaniesByName(companyNameValue);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (
            addRequestSuccessful ||
            editRequestSuccessful ||
            deleteRequestSuccessful
        ) {
            setIsDialogOpen(false);
        }
    }, [
        addRequestSuccessful,
        editRequestSuccessful,
        deleteRequestSuccessful,
        setIsDialogOpen,
    ]);

    const handleDelete = async () => {
        const endpointParams = {
            candidateId: candidateId,
            experienceId: experienceId,
        };
        await deleteExperience(endpointParams);
    };
    const shouldLocationBeDropdown = () => {
        return isLocationUpdated || !locationTitle;
    };
    return (
        <Box component="form" onSubmit={handleSubmit(submitForm)}>
            <Typography variant="header2">{title} </Typography>
            <Stack mt={3} spacing={3}>
                {showExtraOptions ? (
                    <>
                        <Typography variant="header2">
                            Add a new company
                        </Typography>
                        <HorizontalFlex>
                            <TextField
                                variant="standard"
                                fullWidth
                                required
                                name="name"
                                {...register("name")}
                                defaultValue={""}
                                label="Company name"
                            />
                            <Controller
                                name="companySizeId"
                                control={control}
                                defaultValue={null}
                                render={({ field }) => (
                                    <Autocomplete
                                        id="companySizeId"
                                        fullWidth
                                        autoComplete
                                        options={companySizeOptions || []}
                                        getOptionLabel={(option) =>
                                            option?.name || ""
                                        }
                                        isOptionEqualToValue={(option, value) =>
                                            option.id === value.id
                                        }
                                        {...field}
                                        onChange={(event, newValue) => {
                                            field.onChange(newValue);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                variant="standard"
                                                {...params}
                                                label="Company size"
                                            />
                                        )}
                                    />
                                )}
                            />
                        </HorizontalFlex>
                        <HorizontalFlex>
                            <Controller
                                name="industries"
                                control={control}
                                defaultValue={[]}
                                render={({ field }) => (
                                    <Autocomplete
                                        id="industries"
                                        fullWidth
                                        multiple
                                        filterSelectedOptions
                                        autoComplete
                                        options={industriesOptions || []}
                                        getOptionLabel={(option) =>
                                            option.name || ""
                                        }
                                        isOptionEqualToValue={(option, value) =>
                                            option.id === value.id
                                        }
                                        {...field}
                                        onChange={(event, newValue) => {
                                            field.onChange(newValue);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                variant="standard"
                                                {...params}
                                                label="Industry"
                                            />
                                        )}
                                    />
                                )}
                            />
                            <Controller
                                name="companyTypeId"
                                control={control}
                                defaultValue={null}
                                render={({ field }) => (
                                    <Autocomplete
                                        id="companyTypeId"
                                        fullWidth
                                        autoComplete
                                        options={companyTypesOptions || []}
                                        getOptionLabel={(option) =>
                                            option?.name || ""
                                        }
                                        isOptionEqualToValue={(option, value) =>
                                            option.id === value.id
                                        }
                                        {...field}
                                        onChange={(event, newValue) => {
                                            field.onChange(newValue);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                variant="standard"
                                                {...params}
                                                label="Company type"
                                            />
                                        )}
                                    />
                                )}
                            />
                        </HorizontalFlex>
                        <HorizontalFlex>
                            <TextField
                                variant="standard"
                                fullWidth
                                name="websiteUrl"
                                {...register("websiteUrl", {})}
                                defaultValue={""}
                                label="Company url"
                            />
                            <TextField
                                variant="standard"
                                fullWidth
                                name="linkedinUrl"
                                {...register("linkedinUrl", {})}
                                defaultValue={""}
                                label="Company linkedin"
                            />
                        </HorizontalFlex>
                        <Button onClick={handleClick}>
                            Go back to existing companies
                        </Button>
                    </>
                ) : (
                    <></>
                )}

                {/* Company - Position */}
                <HorizontalFlex>
                    {!showExtraOptions ? (
                        <Controller
                            name="companyId"
                            control={control}
                            defaultValue={edit ? experience?.company : null}
                            render={({ field }) => (
                                <Autocomplete
                                    id="companyId"
                                    fullWidth
                                    autoComplete
                                    options={companiesOptions}
                                    loading={fetching}
                                    loadingText={"Loading companies"}
                                    getOptionLabel={(option) =>
                                        option?.name || ""
                                    }
                                    isOptionEqualToValue={(option, value) =>
                                        option.id === value.id
                                    }
                                    {...field}
                                    onChange={(event, newValue) => {
                                        field.onChange(newValue);
                                    }}
                                    noOptionsText={renderNoOptionsComponent}
                                    renderInput={(params) => (
                                        <TextField
                                            required
                                            variant="standard"
                                            onInput={handleCompanyFetch}
                                            {...params}
                                            label="Company"
                                            onChange={(event) => {
                                                const newInputValue =
                                                    event.target.value;
                                                if (
                                                    shouldFetchCompanies(
                                                        newInputValue,
                                                    ) &&
                                                    !fetching
                                                ) {
                                                    setRenderNoOptionsComponent(
                                                        <Button
                                                            onClick={
                                                                handleClick
                                                            }
                                                        >
                                                            No match? Click to
                                                            add company
                                                        </Button>,
                                                    );
                                                } else {
                                                    setRenderNoOptionsComponent(
                                                        "Start typing",
                                                    );
                                                }
                                            }}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {fetching && (
                                                            <CircularProgress
                                                                color="inherit"
                                                                size={20}
                                                            />
                                                        )}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            )}
                        />
                    ) : (
                        <></>
                    )}

                    <TextField
                        variant="standard"
                        fullWidth
                        name="jobTitle"
                        {...register("jobTitle", {})}
                        defaultValue={edit ? experience?.jobTitle : ""}
                        required
                        label="Position"
                    />
                </HorizontalFlex>
                {/* Start - End date */}
                <HorizontalFlex>
                    <TextField
                        variant="standard"
                        {...register("startAt")}
                        fullWidth
                        required
                        InputLabelProps={{
                            shrink: true,
                        }}
                        name="startAt"
                        label="Start Date"
                        type="month"
                    />
                    <TextField
                        variant="standard"
                        {...register("endAt")}
                        name="endAt"
                        disabled={currentlyWorkingStatus}
                        fullWidth
                        InputLabelProps={{
                            shrink: true,
                        }}
                        label="End Date"
                        type="month"
                    />
                </HorizontalFlex>
                <HorizontalFlex>
                    <Controller
                        name="currentlyWorking"
                        control={control}
                        defaultValue={isCurrentlyWorking} // Set the default value to false
                        render={({ field }) => (
                            <FormControlLabel
                                control={
                                    <Switch
                                        {...field}
                                        checked={field.value}
                                        color="primary"
                                    />
                                }
                                label="Currently working there"
                            />
                        )}
                    />
                </HorizontalFlex>
                {!shouldLocationBeDropdown() && (
                <HorizontalFlex sx={{ marginBottom: "1rem" }}>
                    <TextField
                        variant="standard"
                        InputProps={{
                            endAdornment: (
                                <Box sx={{ display: "flex", gap: 1 }}>
                                    <EditIcon
                                        onClick={() => setIsLocationUpdated(true)}
                                        sx={{ cursor: "pointer" }}
                                    />
                                </Box>
                            ),
                        }}
                        sx={{
                            '& .MuiInputBase-input.Mui-disabled': {
                              WebkitTextFillColor: '#000', 
                              opacity: 1,
                            },
                          }}
                        disabled
                        fullWidth
                        name="location"
                        defaultValue={locationTitle || undefined}
                        label="Location"
                    />
                </HorizontalFlex>
            )}

                { shouldLocationBeDropdown() && (
                    <HorizontalFlex sx={{ marginBottom: "1rem" }}>
                        <Controller
                            name="location"
                            control={control}
                            defaultValue={""}
                            render={({ field }) => {
                                return (
                                    <Autocomplete
                                        filterOptions={(x) => x}
                                        id="location"
                                        fullWidth
                                        autoComplete
                                        loading={isSearchLocationsLoading}
                                        noOptionsText="No locations found"
                                        options={locations}
                                        {...field}
                                        getOptionLabel={(option) =>
                                            typeof option === "string"
                                                ? option
                                                : option?.title ?? ""
                                        }
                                        isOptionEqualToValue={(
                                            option,
                                            value,
                                        ) => {
                                            if (typeof option === "string") {
                                                return option === value;
                                            }
                                            return option?.id === value?.id;
                                        }}
                                        onInputChange={
                                            handleLocationInputChange
                                        }
                                        onChange={(_, newValue) => {
                                            field.onChange(newValue);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                variant="standard"
                                                label="Location"
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                            />
                                        )}
                                    />
                                );
                            }}
                        />
                    </HorizontalFlex>
                )}

                {/* Experience skills - Responsibilities */}
                <HorizontalFlex>
                    <Controller
                        name="skills"
                        control={control}
                        fullWidth
                        defaultValue={edit ? experience?.skills : []}
                        render={({ field }) => (
                            <Autocomplete
                                id="skills"
                                fullWidth
                                multiple
                                filterSelectedOptions
                                autoComplete
                                limitTags={1}
                                options={hardSkillsOptions || []}
                                getOptionLabel={(option) => option.name || ""}
                                isOptionEqualToValue={(option, value) =>
                                    option.id === value.id
                                }
                                {...field}
                                onChange={(event, newValue) => {
                                    field.onChange(newValue);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        variant="standard"
                                        {...params}
                                        label="Skills"
                                    />
                                )}
                            />
                        )}
                    />
                </HorizontalFlex>
                {/* Job description */}
                <HorizontalFlex>
                    <TextField
                        variant="standard"
                        defaultValue={edit ? experience?.jobDescription : ""}
                        {...register("jobDescription")}
                        fullWidth
                        multiline
                        name="jobDescription"
                        label="Job description"
                    />
                </HorizontalFlex>

                <HorizontalFlex>
                    <Controller
                        name="occupations"
                        control={control}
                        defaultValue={edit ? experience.occupations : []}
                        render={({ field }) => (
                            <Autocomplete
                                id="occupations"
                                fullWidth
                                multiple
                                filterSelectedOptions
                                autoComplete
                                options={occupationsOptions || []}
                                getOptionLabel={(option) => option.name || ""}
                                isOptionEqualToValue={(option, value) =>
                                    option.id === value.id
                                }
                                {...field}
                                onChange={(event, newValue) => {
                                    field.onChange(newValue);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        variant="standard"
                                        {...params}
                                        label="Occupations"
                                    />
                                )}
                            />
                        )}
                    />
                </HorizontalFlex>
                <HorizontalFlex>
                    <Controller
                        name="jobFunctions"
                        control={control}
                        defaultValue={edit ? experience.job_functions : []}
                        render={({ field }) => (
                            <Autocomplete
                                id="jobFunctions"
                                fullWidth
                                multiple
                                filterSelectedOptions
                                autoComplete
                                options={jobFunctionsOptions || []}
                                getOptionLabel={(option) => option.name || ""}
                                isOptionEqualToValue={(option, value) =>
                                    option.id === value.id
                                }
                                {...field}
                                onChange={(event, newValue) => {
                                    field.onChange(newValue);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        variant="standard"
                                        {...params}
                                        label="Job Functions"
                                    />
                                )}
                            />
                        )}
                    />
                </HorizontalFlex>
                <HorizontalFlex>
                    <Controller
                        name="industries"
                        control={control}
                        defaultValue={edit ? experience.industries : []}
                        render={({ field }) => (
                            <Autocomplete
                                id="industries"
                                fullWidth
                                multiple
                                filterSelectedOptions
                                autoComplete
                                options={industriesOptions || []}
                                getOptionLabel={(option) => option.name || ""}
                                isOptionEqualToValue={(option, value) =>
                                    option.id === value.id
                                }
                                {...field}
                                onChange={(event, newValue) => {
                                    field.onChange(newValue);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        variant="standard"
                                        {...params}
                                        label="Experience Industries"
                                    />
                                )}
                            />
                        )}
                    />
                </HorizontalFlex>
                <HorizontalFlex>
                    <Controller
                        name="jobChangeReason"
                        control={control}
                        defaultValue={edit ? experience?.jobChangeReason : null}
                        render={({ field }) => (
                            <Autocomplete
                                id="jobChangeReason"
                                fullWidth
                                autoComplete
                                options={jobChangeReasonsOptions || []}
                                getOptionLabel={(option) => option.name}
                                value={field?.value || undefined}
                                isOptionEqualToValue={(option, value) =>
                                    option.name === value.name
                                }
                                {...field}
                                onChange={(event, newValue) => {
                                    field.onChange(newValue);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        variant="standard"
                                        {...params}
                                        label="Reason for job change"
                                    />
                                )}
                            />
                        )}
                    />
                    <Controller
                        name="jobType"
                        control={control}
                        defaultValue={edit ? experience?.jobType : null}
                        render={({ field }) => (
                            <Autocomplete
                                id="jobType"
                                fullWidth
                                autoComplete
                                options={jobTypeOptions || []}
                                getOptionLabel={(option) => option?.name || ""}
                                isOptionEqualToValue={(option, value) =>
                                    option.id === value.id
                                }
                                {...field}
                                onChange={(event, newValue) => {
                                    field.onChange(newValue);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        variant="standard"
                                        {...params}
                                        label="Job type"
                                    />
                                )}
                            />
                        )}
                    />
                </HorizontalFlex>
                <Stack direction="row" justifyContent="space-between">
                    {edit && (
                        <MeteredButton
                            onActionComplete={handleDelete}
                            loading={isDeleteLoading}
                            buttonProps={{
                                color: "gray",
                                startIcon: <DeleteOutlinedIcon />,
                            }}
                        >
                            Delete
                        </MeteredButton>
                    )}

                    <LoadingButton
                        sx={{ marginLeft: "auto" }}
                        color="primary"
                        type="submit"
                        loading={isLoading}
                        loadingPosition="start"
                        startIcon={<SaveIcon />}
                    >
                        Save changes
                    </LoadingButton>
                </Stack>
            </Stack>
        </Box>
    );
};

export default ExperienceForm;
